import type { StepUpKind } from '@fos/shared/contracts/identity';
import { ApiError, appErrorCode } from './api';

/**
 * Step-up („tryb sudo”, docs/API_AUTH.md): usługa rzuca STEP_UP_REQUIRED → SPA pokazuje modal → potwierdzenie →
 * ponawia akcję. `error.details.kind` mówi, czym potwierdzić: `password` („Potwierdź hasłem”, 10 min) albo
 * `second_factor` (Fala 7: kod z maila — sejf, przełącznik 2FA, sesja po podniesieniu uprawnień). Magazyn jest poza
 * Reactem, żeby mógł go użyć także link tRPC (lib/trpc.ts). Modal renderuje `StepUpDialog` (app/StepUpDialog.tsx).
 */

type Listener = () => void;

interface Pending {
  kind: StepUpKind;
  promise: Promise<boolean>;
  resolve: (confirmed: boolean) => void;
}

let pending: Pending | null = null;
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) listener();
}

export const stepUpStore = {
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** Czy modal jest otwarty. */
  isOpen(): boolean {
    return pending !== null;
  },
  /** Czym potwierdzić w otwartym modalu (domyślnie hasłem). */
  kind(): StepUpKind {
    return pending?.kind ?? 'password';
  },
  /** Otwiera modal (albo dołącza do już otwartego). true = potwierdzono. */
  request(kind: StepUpKind = 'password'): Promise<boolean> {
    if (pending) return pending.promise;
    let resolve!: (confirmed: boolean) => void;
    const promise = new Promise<boolean>((r) => {
      resolve = r;
    });
    pending = { kind, promise, resolve };
    emit();
    return promise;
  },
  settle(confirmed: boolean): void {
    const current = pending;
    pending = null;
    emit();
    current?.resolve(confirmed);
  },
};

/** Rodzaj potwierdzenia z błędu STEP_UP_REQUIRED (REST `error.details.kind`, tRPC `error.data.details.kind`). */
export function stepUpKindOf(err: unknown): StepUpKind {
  const details =
    err instanceof ApiError ? err.details : (err as { data?: { details?: unknown } } | null)?.data?.details;
  return (details as { kind?: unknown } | undefined)?.kind === 'second_factor' ? 'second_factor' : 'password';
}

/** Wykonuje akcję; przy STEP_UP_REQUIRED prosi o potwierdzenie i ponawia ją raz. Anulowanie = pierwotny błąd. */
export async function withStepUp<T>(action: () => Promise<T>): Promise<T> {
  try {
    return await action();
  } catch (err) {
    if (appErrorCode(err) !== 'STEP_UP_REQUIRED') throw err;
    const confirmed = await stepUpStore.request(stepUpKindOf(err));
    if (!confirmed) throw err;
    return action();
  }
}

/**
 * Hook dla komponentów. Zapytania i mutacje tRPC obsługuje automatycznie `stepUpLink` (lib/trpc.ts),
 * więc `withStepUp` jest potrzebne tylko dla wywołań REST (`apiFetch`), a `requestStepUp` — gdy akcja
 * wymaga potwierdzenia z góry.
 */
export function useStepUp(): {
  withStepUp: typeof withStepUp;
  requestStepUp: (kind?: StepUpKind) => Promise<boolean>;
} {
  return { withStepUp, requestStepUp: stepUpStore.request };
}
